import moment from 'moment';

import { callIndesignLinerResponse } from 'utils/indesign';
import { IDRequest } from 'lib/types/notice';
import { getAdTemplateCacheID } from 'lib/types/templates';
import {
  EOrganization,
  ESnapshotExists,
  ETemplate,
  exists,
  FirebaseTimestamp
} from 'lib/types';
import { generateFormattedFooter } from 'lib/headers_footers/footers';
import { dateTimeLikeToTimestamp } from 'lib/date';
import * as headers from 'lib/headers_footers/headers';
import { getFirebaseContext } from 'utils/firebase';
import { mockRef } from 'lib/testing/firebaseMockUtils';

export type LayoutSettings = Pick<
  EOrganization,
  | 'headerFormatString'
  | 'footerFormatString'
  | 'oneRunFooter'
  | 'oneRunHeader'
  | 'linerMinColumns'
  | 'linerMaxColumns'
  | 'fullPageTemplate'
  | 'displayMinColumns'
  | 'displayMaxColumns'
  | 'linerBorder'
  | 'noticeHeaders'
>;

/**
 * Extracts the layout settings from an organization
 * @param organization
 * @returns {LayoutSettings} relevant attributes for basic layout configuration
 */
export const getLayoutSettingsFromOrganization = (
  organization: ESnapshotExists<EOrganization>
): LayoutSettings => {
  const {
    headerFormatString,
    footerFormatString,
    displayMinColumns,
    displayMaxColumns,
    linerMinColumns,
    linerMaxColumns,
    linerBorder,
    fullPageTemplate,
    oneRunHeader,
    oneRunFooter,
    noticeHeaders
  } = organization.data();
  return {
    headerFormatString,
    footerFormatString,
    displayMinColumns,
    displayMaxColumns,
    linerMinColumns,
    linerMaxColumns,
    linerBorder,
    fullPageTemplate,
    oneRunHeader,
    oneRunFooter,
    noticeHeaders
  };
};

/**
 * Creates a set of sample publication dates for the notice we are considering
 * @param numberOfRuns how many dates to create
 * @returns {FirebaseTimestamp[]} Array of publication dates as Firebase timestamps
 */
const generateSyntheticPublicationDateTimestamps = (numberOfRuns: number) => {
  const publicationDates = [];
  for (let i = 0; i < numberOfRuns; i++) {
    const nextDate = moment().add(i, 'weeks').toDate();
    publicationDates.push(
      dateTimeLikeToTimestamp(nextDate) as FirebaseTimestamp
    );
  }
  return publicationDates;
};

/**
 * Determines the relevant header to pull for the notice we are considering
 * @param layoutSettings Layout settings for the organization we are looking at
 * @param publicationDates Publication dates associated with that organization
 * @returns {string} The header to use for the notice
 */
const getHeaderFromLayoutSettings = (
  layoutSettings: LayoutSettings,
  numRuns: number
): string | undefined => {
  const { headerFormatString, oneRunHeader } = layoutSettings;
  if (oneRunHeader && numRuns === 1) {
    return oneRunHeader;
  }
  return headerFormatString;
};

/**
 * Creates a sample dynamic header for the notice we are considering
 * @param layoutSettings Layout settings for the relevant paper
 * @param numberOfRuns Number of times we are pretending the notice is running
 * @returns {string} The header to use for the notice
 */
const getDynamicHeaderFromLayoutSettingsAndNumberOfRuns = (
  layoutSettings: LayoutSettings,
  numberOfRuns: number
): string | null => {
  const publicationTimestamps =
    generateSyntheticPublicationDateTimestamps(numberOfRuns);
  const relevantHeader = getHeaderFromLayoutSettings(
    layoutSettings,
    numberOfRuns
  );
  const dynamicHeaders = relevantHeader
    ? headers.generate(relevantHeader, publicationTimestamps, null)
    : null;
  return dynamicHeaders?.length ? dynamicHeaders[0] : null;
};

/**
 * Generates a sample dynamic footer for the notice we are considering
 * @param activeOrganization Paper we are looking at
 * @param numberOfRuns How many times should we pretend the notice is running
 * @returns {string} The footer to use for the notice
 */
const getDynamicFooterFromLayoutSettingsAndNumberOfRuns = async (
  layoutSettings: LayoutSettings,
  numberOfRuns: number
): Promise<string> => {
  const publicationTimestamps =
    generateSyntheticPublicationDateTimestamps(numberOfRuns);

  // TODO: This is still really nasty and we should not do this, but we need to significantly
  // untangle the `generateFormattedFooter` function to do so.
  const mockNewspaperRef = mockRef<EOrganization>(
    'fake-newspaper',
    layoutSettings
  );
  const footer = await generateFormattedFooter(
    getFirebaseContext(),
    {
      publicationDates: publicationTimestamps,
      newspaper: mockNewspaperRef,
      customId: '123456'
    },
    undefined,
    window.DOMParser
  );

  return footer;
};

/**
 * Sample HTML values that can be used to test notice content.
 * TODO: We should consult with the implementation team to determine what other defaults we should load.
 */
export const SAMPLE_NOTICE_CONTENT_OPTIONS = [
  {
    label: 'Default notice content',
    value:
      '<div>This is on the left</div><div custom-style="align-right" data-mce-custom-style="align-right">This is on the right</div><div custom-style="align-center" data-mce-custom-style="align-center">This is in the center</div><div><strong>This is bold</strong></div><div><em>This is in italic</em></div><div><strong><em>This is bold and italic</em></strong></div><div><span custom-style="Underline">This is underlined</span></div><div><span custom-style="Underline"><em>This is underlined and italic</em></span></div><div><strong><span custom-style="Underline">This is underlined and bold</span></strong></div><div>This line is a bit longer and likely extends over a single line and into two lines but if this is a two column notice may not!</div>'
  },
  {
    label: 'Long notice content',
    value:
      '<div>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Mauris a diam maecenas sed enim ut sem. Odio aenean sed adipiscing diam donec adipiscing tristique. Dui vivamus arcu felis bibendum. Sed sed risus pretium quam. Orci phasellus egestas tellus rutrum tellus pellentesque eu. Arcu odio ut sem nulla. Euismod nisi porta lorem mollis aliquam. Mauris augue neque gravida in. At risus viverra adipiscing at in tellus integer feugiat scelerisque. Ut faucibus pulvinar elementum integer enim neque. Cras adipiscing enim eu turpis egestas pretium aenean. Nec feugiat in fermentum posuere urna nec. Elementum sagittis vitae et leo. Sagittis id consectetur purus ut faucibus pulvinar elementum integer enim. Quisque non tellus orci ac auctor augue mauris augue neque. Vitae purus faucibus ornare suspendisse sed. Vestibulum mattis ullamcorper velit sed ullamcorper morbi. Viverra nibh cras pulvinar mattis nunc. Risus quis varius quam quisque id diam vel quam elementum. In fermentum posuere urna nec tincidunt praesent semper feugiat. Convallis a cras semper auctor neque vitae tempus quam. Nisi quis eleifend quam adipiscing vitae. Felis imperdiet proin fermentum leo vel orci porta. Erat velit scelerisque in dictum non. Praesent tristique magna sit amet purus. Nisi scelerisque eu ultrices vitae auctor eu augue ut. Nunc sed velit dignissim sodales ut eu. Metus vulputate eu scelerisque felis imperdiet. Faucibus et molestie ac feugiat sed lectus vestibulum mattis. Vitae congue eu consequat ac felis.        Dolor sit amet consectetur adipiscing elit. Turpis nunc eget lorem dolor sed viverra. Volutpat est velit egestas dui id ornare. Egestas erat imperdiet sed euismod. Ultricies mi quis hendrerit dolor magna. Scelerisque eu ultrices vitae auctor. Eget felis eget nunc lobortis mattis aliquam faucibus. Tellus integer feugiat scelerisque varius morbi enim nunc faucibus. Nunc sed augue lacus viverra vitae. Orci dapibus ultrices in iaculis nunc sed augue lacus. Nisl condimentum id venenatis a condimentum vitae sapien pellentesque habitant. Felis bibendum ut tristique et egestas quis ipsum suspendisse ultrices. Urna neque viverra justo nec.        Est ullamcorper eget nulla facilisi. Eget est lorem ipsum dolor sit. Ultrices sagittis orci a scelerisque purus semper eget duis. Sapien eget mi proin sed libero enim. Lacus sed viverra tellus in hac habitasse. Aliquam ut porttitor leo a diam sollicitudin tempor. Morbi tincidunt augue interdum velit euismod in pellentesque. Tristique nulla aliquet enim tortor at auctor urna nunc. Et sollicitudin ac orci phasellus egestas tellus rutrum tellus pellentesque. Senectus et netus et malesuada fames.        Dictum sit amet justo donec enim diam. Nulla pellentesque dignissim enim sit amet venenatis urna cursus. Senectus et netus et malesuada fames ac turpis egestas integer. Fusce id velit ut tortor. Maecenas sed enim ut sem viverra aliquet eget sit. Tellus cras adipiscing enim eu turpis egestas pretium. Viverra accumsan in nisl nisi scelerisque. Eu tincidunt tortor aliquam nulla facilisi cras fermentum odio. Sit amet mauris commodo quis imperdiet massa tincidunt. Enim eu turpis egestas pretium aenean pharetra magna ac placerat. Enim nunc faucibus a pellentesque sit amet porttitor. Porta lorem mollis aliquam ut porttitor leo a diam. Lectus vestibulum mattis ullamcorper velit sed ullamcorper morbi. Semper auctor neque vitae tempus. Bibendum arcu vitae elementum curabitur vitae. Lacus sed viverra tellus in hac.        Gravida quis blandit turpis cursus in hac habitasse platea. Consectetur lorem donec massa sapien faucibus et. Imperdiet massa tincidunt nunc pulvinar sapien et ligula ullamcorper malesuada. Morbi leo urna molestie at. Ac tortor vitae purus faucibus. Facilisi nullam vehicula ipsum a arcu cursus vitae. Et malesuada fames ac turpis egestas maecenas. Faucibus scelerisque eleifend donec pretium vulputate. Lorem ipsum dolor sit amet consectetur adipiscing elit duis tristique. Risus pretium quam vulputate dignissim suspendisse in est. Et tortor at risus viverra adipiscing at in tellus integer. Felis eget velit aliquet sagittis id consectetur purus ut. Dignissim convallis aenean et tortor at risus viverra. Et pharetra pharetra massa massa. Eget arcu dictum varius duis at. Eu sem integer vitae justo eget magna fermentum. Posuere ac ut consequat semper viverra nam. Feugiat in fermentum posuere urna nec tincidunt. In eu mi bibendum neque egestas congue quisque egestas.        Gravida cum sociis natoque penatibus. Et netus et malesuada fames ac turpis egestas maecenas pharetra. Erat velit scelerisque in dictum. Maecenas accumsan lacus vel facilisis volutpat est velit. Vitae auctor eu augue ut. Risus in hendrerit gravida rutrum quisque non tellus. Proin libero nunc consequat interdum varius. At erat pellentesque adipiscing commodo elit at imperdiet dui accumsan. Blandit volutpat maecenas volutpat blandit aliquam etiam erat. Odio ut sem nulla pharetra diam sit amet nisl. Elementum tempus egestas sed sed. Aliquam faucibus purus in massa tempor nec feugiat nisl. Suspendisse ultrices gravida dictum fusce ut. Tristique et egestas quis ipsum. Ac tortor dignissim convallis aenean. Eget mauris pharetra et ultrices. Laoreet id donec ultrices tincidunt arcu non sodales neque. Aliquam sem fringilla ut morbi.        Lectus magna fringilla urna porttitor. Bibendum arcu vitae elementum curabitur vitae nunc sed velit. Ultrices vitae auctor eu augue. Duis at tellus at urna condimentum mattis. Erat velit scelerisque in dictum non consectetur a erat nam. Egestas pretium aenean pharetra magna ac placerat vestibulum lectus mauris. Varius quam quisque id diam vel quam elementum pulvinar. Lorem ipsum dolor sit amet consectetur. Leo a diam sollicitudin tempor id. Ac felis donec et odio pellentesque diam volutpat commodo. Tortor id aliquet lectus proin nibh. Cursus euismod quis viverra nibh cras pulvinar mattis nunc. Cursus sit amet dictum sit amet justo. Amet commodo nulla facilisi nullam vehicula. Viverra maecenas accumsan lacus vel facilisis volutpat. Lorem ipsum dolor sit amet. Lacus suspendisse faucibus interdum posuere lorem ipsum dolor. Adipiscing tristique risus nec feugiat in fermentum posuere urna nec. Sed turpis tincidunt id aliquet risus feugiat. Enim nec dui nunc mattis. Non curabitur gravida arcu ac tortor dignissim convallis aenean. Nam aliquam sem et tortor consequat id porta. Duis ultricies lacus sed turpis tincidunt id aliquet risus. In massa tempor nec feugiat nisl pretium fusce id velit. Adipiscing diam donec adipiscing tristique risus nec feugiat in. Sed nisi lacus sed viverra. Vitae congue eu consequat ac felis donec et odio. Purus semper eget duis at tellus at. Ut sem nulla pharetra diam sit. Eu augue ut lectus arcu bibendum at varius vel pharetra. Velit sed ullamcorper morbi tincidunt ornare massa eget egestas purus. Vitae suscipit tellus mauris a diam maecenas. Bibendum est ultricies integer quis auctor elit sed. Blandit libero volutpat sed cras ornare arcu dui vivamus. Sit amet dictum sit amet justo. Sit amet aliquam id diam. Dolor magna eget est lorem ipsum dolor sit amet consectetur. Placerat in egestas erat imperdiet sed. Turpis egestas maecenas pharetra convallis posuere. Egestas purus viverra accumsan in nisl nisi scelerisque eu ultrices. Blandit cursus risus at ultrices mi tempus imperdiet nulla malesuada. Eget velit aliquet sagittis id consectetur. At consectetur lorem donec massa. Pharetra vel turpis nunc eget lorem dolor sed.</div>'
  }
];

export const SAMPLE_NOTICE_COLUMN_OPTIONS = [
  { value: '1', label: 'One column' },
  { value: '2', label: 'Two columns' },
  { value: '3', label: 'Three columns' },
  { value: '4', label: 'Four columns' },
  { value: '5', label: 'Five columns' },
  { value: '6', label: 'Six columns' }
];

export const SAMPLE_NOTICE_PUBLICATION_OPTIONS = [
  { value: '1', label: 'One publication date' },
  { value: '2', label: 'Two publication dates' },
  { value: '3', label: 'Three publication dates' },
  { value: '4', label: 'Four publication dates' }
];

/**
 * Generates a preview of a notice using the given template, notice content, and details of the run.
 * This is used in Template settings pages to preview templates without going to the placement flow.
 */
export const generateNoticePreview = async (
  template: ESnapshotExists<ETemplate>,
  layoutSettings: LayoutSettings,
  options: {
    previewContent: string;
    format: 'jpg' | 'rtf' | 'idml' | 'pdf';
    outputToBase64: boolean;
    totalRuns: number;
    columnWidth: number;
  }
) => {
  const { previewContent, format, outputToBase64, totalRuns, columnWidth } =
    options;

  const dynamicHeader = getDynamicHeaderFromLayoutSettingsAndNumberOfRuns(
    layoutSettings,
    totalRuns
  );
  const dynamicFooter = await getDynamicFooterFromLayoutSettingsAndNumberOfRuns(
    layoutSettings,
    totalRuns
  );

  const organizationSnap = await template.data().organization?.get();

  const isPaperEnabledForCustomHeader =
    exists(organizationSnap) &&
    !!organizationSnap.data().noticeHeaders?.enabled;

  // Adds a new paragraph at start of notice content if the template setup for heading styles.
  // This is a <div> and not <p> because here we do not need to translate html into indesign html as indesign accepts div
  const testNoticePreviewContent = isPaperEnabledForCustomHeader
    ? `<div>heading</div>${previewContent}`
    : previewContent;
  const noticeHtml = testNoticePreviewContent + dynamicFooter;

  const serverParameters: IDRequest = {
    linerMaxColumns: columnWidth,
    optimizeColumns: false,
    outputToBase64,
    dynamicHeader,
    dynamicFooter,
    downloadUrl: template.data().downloadUrl,
    noticeId: 'test',
    quality: 'high',
    columns: columnWidth,
    formats: [format],
    html: noticeHtml,
    id: getAdTemplateCacheID(template),
    customHeader: isPaperEnabledForCustomHeader
  };
  const indesignResponse = await callIndesignLinerResponse({
    request: serverParameters,
    options: outputToBase64 ? {} : { responseType: 'arraybuffer' }
  });
  return indesignResponse.data;
};
